import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import { 
  Plane,
  Clock,
  MapPin,
  Calendar,
  User,
  TriangleAlert,
  CheckCircle2,
  QrCode,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

const PALETTE = TEMPLATE_PALETTES.boarding_pass;

const BoardingPassTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // Extract airport codes
  const getAirportCode = (text) => {
    if (!text) return '';
    // Try to extract 3-letter airport code
    const match = text.match(/\b[A-Z]{3}\b/);
    return match ? match[0] : text.substring(0, 3).toUpperCase();
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.lastName.toUpperCase()}, ${quote.client.firstName.toUpperCase()}`;
    }
    return 'PASSENGER';
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Minimal Header - No Hero */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wider mb-1">Flight Itinerary</div>
              <h1 className="text-2xl font-bold text-[var(--foreground)] break-words">{quote.quoteTitle || 'Your Flight Details'}</h1>
            </div>
            <div className="text-right">
              <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wider mb-1">Booking Ref</div>
              <div className="font-mono text-sm font-bold text-[var(--foreground)] break-all">{quote.quoteNumber}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Passenger Info Bar */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[var(--muted-foreground)]" />
              <span className="text-sm text-[var(--muted-foreground)]">Passenger:</span>
              <span className="font-mono text-sm font-semibold text-[var(--foreground)]">{getClientName()}</span>
            </div>
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[var(--muted-foreground)]">Total Travelers:</span>
                <span className="font-semibold text-[var(--foreground)]">
                  {quote.travelers.adults || 0} + {quote.travelers.children?.length || 0}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Boarding Passes */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {quote.components && quote.components.length > 0 ? (
          <div className="space-y-6">
            {quote.components.map((component, index) => {
              // Extract flight details
              const departureCity = component.data?.departure_city || component.data?.origin || '';
              const arrivalCity = component.data?.arrival_city || component.data?.destination || '';
              const departureCode = getAirportCode(departureCity);
              const arrivalCode = getAirportCode(arrivalCity);
              const departureTime = component.data?.departure_time || '';
              const arrivalTime = component.data?.arrival_time || '';
              const flightDate = component.data?.date || component.data?.departure_date || '';
              const airline = component.data?.airline || '';
              const flightNumber = component.data?.flight_number || '';
              const terminal = component.data?.terminal || '';
              const gate = component.data?.gate || '';
              const layoverDuration = component.data?.layover_duration || component.data?.layover || '';
              
              // Check for long layover (>4 hours)
              const isLongLayover = layoverDuration && (
                layoverDuration.includes('5h') || layoverDuration.includes('6h') || 
                layoverDuration.includes('7h') || layoverDuration.includes('8h') ||
                parseInt(layoverDuration) > 4
              );

              return (
                <React.Fragment key={component.id || index}>
                  {/* Boarding Pass Card */}
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-[var(--border)]">
                    {/* Header Strip */}
                    <div className="bg-[var(--primary)] px-6 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Plane className="w-5 h-5 text-[var(--background)]" />
                        <span className="text-[var(--background)] font-semibold uppercase tracking-wide">
                          {airline || 'Flight'}
                        </span>
                      </div>
                      {flightNumber && (
                        <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded text-[var(--background)] font-mono text-sm">
                          {flightNumber}
                        </span>
                      )}
                    </div>

                    {/* Main Boarding Pass Content */}
                    <div className="p-6">
                      {/* Airport Codes and Route */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="text-center">
                          <div className="text-5xl font-black text-[var(--foreground)] mb-2">{departureCode}</div>
                          <div className="text-sm text-[var(--muted-foreground)]">{departureCity}</div>
                        </div>
                        
                        <div className="flex-1 mx-8 relative">
                          <div className="border-t-2 border-dashed border-[var(--border)] relative">
                            <Plane className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-[var(--accent)] bg-white px-1" />
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-5xl font-black text-[var(--foreground)] mb-2">{arrivalCode}</div>
                          <div className="text-sm text-[var(--muted-foreground)]">{arrivalCity}</div>
                        </div>
                      </div>

                      {/* Flight Details Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                        {/* Date */}
                        {flightDate && (
                          <div className="text-center p-3 bg-[var(--muted)] rounded">
                            <Calendar className="w-4 h-4 text-[var(--muted-foreground)] mx-auto mb-2" />
                            <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-1">Date</div>
                            <div className="font-mono text-sm font-semibold text-[var(--foreground)]">{formatDate(flightDate)}</div>
                          </div>
                        )}

                        {/* Departure Time */}
                        {departureTime && (
                          <div className="text-center p-3 bg-[var(--muted)] rounded">
                            <Clock className="w-4 h-4 text-[var(--muted-foreground)] mx-auto mb-2" />
                            <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-1">Departs</div>
                            <div className="font-mono text-lg font-bold text-[var(--foreground)]">{formatTime(departureTime)}</div>
                          </div>
                        )}

                        {/* Arrival Time */}
                        {arrivalTime && (
                          <div className="text-center p-3 bg-[var(--muted)] rounded">
                            <Clock className="w-4 h-4 text-[var(--muted-foreground)] mx-auto mb-2" />
                            <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-1">Arrives</div>
                            <div className="font-mono text-lg font-bold text-[var(--foreground)]">{formatTime(arrivalTime)}</div>
                          </div>
                        )}

                        {/* Terminal/Gate */}
                        {(terminal || gate) && (
                          <div className="text-center p-3 bg-[var(--muted)] rounded">
                            <MapPin className="w-4 h-4 text-[var(--muted-foreground)] mx-auto mb-2" />
                            <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-1">Gate</div>
                            <div className="font-mono text-lg font-bold text-[var(--foreground)]">
                              {terminal && `T${terminal}`}{gate && `-${gate}`}
                              {!terminal && !gate && 'TBA'}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Additional Flight Info */}
                      {component.data && Object.keys(component.data).length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                          {Object.entries(component.data).map(([key, value]) => {
                            if (!value || 
                                key === 'isOption' || 
                                key === 'optionDescription' ||
                                key === 'departure_city' ||
                                key === 'arrival_city' ||
                                key === 'origin' ||
                                key === 'destination' ||
                                key === 'departure_time' ||
                                key === 'arrival_time' ||
                                key === 'date' ||
                                key === 'departure_date' ||
                                key === 'airline' ||
                                key === 'flight_number' ||
                                key === 'terminal' ||
                                key === 'gate') {
                              return null;
                            }
                            
                            // Check if this is a price field
                            const isPriceField = key.toLowerCase().includes('price') || 
                                                 key.toLowerCase().includes('cost') || 
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            
                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }
                            
                            return (
                              <div key={key} className="flex justify-between gap-3 p-2 bg-[var(--muted)] rounded">
                                <span className="min-w-0 break-words text-[var(--muted-foreground)] uppercase tracking-wide">{humanizeKey(key)}:</span>
                                <span className="min-w-0 break-words text-right font-semibold text-[var(--foreground)]">{displayValue}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Price */}
                      <div className="mt-6 pt-6 border-t border-[var(--border)] flex flex-wrap justify-between items-center gap-2">
                        <span className="text-[var(--muted-foreground)] font-semibold">Flight Cost</span>
                        <span className="text-2xl font-bold text-[var(--foreground)]">{formatCurrency(component.price)}</span>
                      </div>

                      {/* Component Photos */}
                      {component.photos && component.photos.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-[var(--border)]">
                          <h4 className="text-sm font-bold text-[var(--foreground)] mb-3 uppercase tracking-wide flex items-center gap-2">
                            <span>📸</span> Photos
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {component.photos.map((photo, photoIndex) => (
                              <div 
                                key={photoIndex} 
                                className="relative group cursor-pointer rounded overflow-hidden shadow hover:shadow-md transition-shadow border-2 border-[var(--border)] bg-white"
                                onClick={() => window.open(photo.url, '_blank')}
                              >
                                <img 
                                  src={photo.url} 
                                  alt={photo.name || 'Component photo'} 
                                  className="w-full h-auto object-contain"
                                  style={{ maxHeight: '300px' }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1">
                                  <span className="text-[var(--background)] text-xs font-semibold">{photo.name || 'View'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Component Documents */}
                      {component.documents && component.documents.length > 0 && (
                        <div className="mt-4">
                          <h4 className="text-sm font-bold text-[var(--foreground)] mb-2 uppercase tracking-wide flex items-center gap-2">
                            <span>📋</span> Documents
                          </h4>
                          <div className="space-y-1">
                            {component.documents.map((doc, docIndex) => (
                              <a 
                                key={docIndex}
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-[var(--muted)] hover:bg-[var(--muted)] px-3 py-2 rounded transition-colors border border-[var(--border)] text-sm"
                              >
                                <span>📄</span>
                                <span className="text-[var(--foreground)] font-medium">{doc.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {hasMultipleOptions(component) && (
                        <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                          <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {getComponentOptions(component).map((option, optIndex) => (
                              <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                                {option.photoUrl && (
                                  <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                                )}
                                <div className="p-4">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                    {option.badge && (
                                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                    )}
                                  </div>
                                  {option.description && (
                                    <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                  )}
                                  <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Barcode Footer */}
                    <div className="bg-[var(--muted)] px-6 py-4 border-t-2 border-dashed border-[var(--border)]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <QrCode className="w-8 h-8 text-[var(--muted-foreground)]" />
                          <div className="text-xs text-[var(--muted-foreground)]">
                            <div className="font-mono font-semibold text-[var(--foreground)]">{quote.quoteNumber}</div>
                            <div>Scan at airport kiosk</div>
                          </div>
                        </div>
                        <div className="font-mono text-xs text-[var(--muted-foreground)]">
                          BOARDING PASS {index + 1} OF {quote.components.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Layover Warning */}
                  {layoverDuration && index < quote.components.length - 1 && (
                    <div className={`flex items-center justify-center gap-3 py-4 px-6 rounded-lg ${
                      isLongLayover ? 'bg-amber-50 border border-amber-200' : 'bg-[var(--muted)] border border-[var(--border)]'
                    }`}>
                      {isLongLayover ? (
                        <TriangleAlert className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-[var(--accent)]" />
                      )}
                      <span className={`font-semibold ${isLongLayover ? 'text-amber-900' : 'text-[var(--foreground)]'}`}>
                        Layover: {layoverDuration}
                      </span>
                      {isLongLayover && (
                        <span className="text-amber-700 text-sm">(Extended layover - plan accordingly)</span>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <Plane className="w-16 h-16 text-[var(--border)] mx-auto mb-4" />
            <p className="text-[var(--muted-foreground)]">No flights added to this itinerary</p>
          </div>
        )}

        {/* Notes */}
        {quote.notes && (
          <div className="mt-8 bg-[var(--muted)] border border-[var(--border)] rounded-lg p-6">
            <h3 className="font-semibold text-[var(--foreground)] mb-3 flex items-center gap-2">
              <TriangleAlert className="w-5 h-5" />
              Important Travel Information
            </h3>
            <p className="text-[var(--foreground)] text-sm leading-relaxed whitespace-pre-wrap">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Total Investment */}
        <div className="mt-8 bg-white rounded-lg shadow-lg overflow-hidden border border-[var(--border)]">
          <div className="bg-[var(--primary)] px-6 py-4">
            <h3 className="text-[var(--background)] font-semibold uppercase tracking-wide">Total Flight Cost</h3>
          </div>
          
          <div className="p-6">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
              <span className="text-[var(--muted-foreground)] text-lg">Total Investment</span>
              <span className="text-3xl sm:text-4xl font-bold text-[var(--foreground)]">{formatCurrency(quote.totalPrice)}</span>
            </div>

            {quote.depositAmount > 0 && (
              <div className="pt-6 border-t border-[var(--border)] grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-[var(--muted)] rounded p-4">
                  <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-1">Deposit Required</div>
                  <div className="text-2xl font-bold text-[var(--foreground)]">{formatCurrency(quote.depositAmount)}</div>
                </div>
                {quote.depositDueDate && (
                  <div className="bg-[var(--muted)] rounded p-4">
                    <div className="text-xs text-[var(--muted-foreground)] uppercase tracking-wide mb-1">Due Date</div>
                    <div className="text-xl font-semibold text-[var(--foreground)]">{formatDate(quote.depositDueDate)}</div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Button */}
            {onPayment && (
              <button
                onClick={onPayment}
                disabled={isAgentPreview}
                className={`w-full bg-[var(--primary)] hover:brightness-110 text-[var(--background)] font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-3 ${
                  isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl'
                }`}
              >
                <CheckCircle2 className="w-5 h-5" />
                {isAgentPreview ? 'Preview Mode' : 'Confirm Booking'}
              </button>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-8 bg-white rounded-lg shadow-md border-2 border-[var(--border)] overflow-hidden">
            <div className="bg-[var(--primary)] px-6 py-3">
              <div className="flex items-center justify-center gap-2 text-[var(--background)]">
                <Plane className="w-5 h-5" />
                <h3 className="text-base font-bold uppercase tracking-wide">Your Travel Agent</h3>
              </div>
            </div>
            
            <div className="p-5">
              {quote.agent_name && (
                <div className="text-center mb-3">
                  <p className="text-lg font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-[var(--muted-foreground)] font-semibold text-sm">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-2 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-[var(--muted)] px-4 py-2.5 rounded hover:bg-[var(--muted)] transition-colors border border-[var(--border)]"
                  >
                    <Mail className="w-4 h-4 text-[var(--accent)]" />
                    <span className="text-[var(--foreground)] font-medium text-sm">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-[var(--muted)] px-4 py-2.5 rounded hover:bg-[var(--muted)] transition-colors border border-[var(--border)]"
                  >
                    <Phone className="w-4 h-4 text-[var(--accent)]" />
                    <span className="text-[var(--foreground)] font-medium text-sm">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoardingPassTemplate;
